import { AfterViewInit, Component, effect, ElementRef, inject, OnDestroy, untracked, viewChild, viewChildren } from '@angular/core';
import { CVInfoWindowContentComponent } from './cv-info-window-content/cv-info-window-content.component';
import { GoogleMap, MapAdvancedMarker, MapInfoWindow, MapMarkerClusterer } from '@angular/google-maps';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatProgressBar } from '@angular/material/progress-bar';
import { NgStyle } from '@angular/common';
import { CvMarkerType, MAP_OPTIONS, MapMarkerData } from '../map-utils';
import { AUTOCOMPLETE_MARKER_ID } from '../../constants';
import { cloneDeep } from 'lodash';
import { Store } from '@ngrx/store';
import { MapActions } from '../state/map-actions';
import { selectAreMarkersMinimized, selectIsCvButtonActive, selectIsStreetViewActive, selectIsStreetViewClosedByUser, selectShowValidationHistory } from '../state/map-selectors';
import { PlaceAutocompleteMarkerEvent } from '../default-map/map-ui/map-ui.component';
import { CvMapUiComponent } from './cv-map-ui/cv-map-ui.component';
import { cvMapSignalStore } from './cv-map-signal-store';
import { CvMarkerComponent } from './cv-marker/cv-marker.component';
import { CvMapService } from './cv-map.service';
import { selectFocusedLocation, selectValidationHistory, selectValidationHistoryLoading } from '../../location-list/state/location-list-selectors';
import { selectRightPanelRelativeWidth } from '../../state/locations-skeleton-selectors';
import { selectCanContinueAfterValidation } from '../state/map-combined.selectors';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatestWith, Subject } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-cv-map',
  imports: [
    CVInfoWindowContentComponent,
    GoogleMap,
    MapInfoWindow,
    MatIcon,
    MatIconButton,
    MatProgressBar,
    NgStyle,
    CvMapUiComponent,
    CvMarkerComponent,
    MapMarkerClusterer,
  ],
  templateUrl: './cv-map.component.html',
  styleUrl: './cv-map.component.scss',
  providers: [cvMapSignalStore, CvMapService],
})
export class CvMapComponent implements OnDestroy, AfterViewInit {
  protected readonly infoWindow = viewChild<MapInfoWindow>(MapInfoWindow); // access info window instance
  protected readonly googleMapDirective = viewChild<GoogleMap>(GoogleMap); // access gmaps instance
  protected readonly streetViewContainer = viewChild<ElementRef>('streetViewContainer');
  protected readonly clustererDirective = viewChild<MapMarkerClusterer>(MapMarkerClusterer);

  // clusterable advanced markers - need to be
  clusterableCvMarkers = viewChildren<CvMarkerComponent>('cvMarkerComponent');

  private readonly signalStore = inject(cvMapSignalStore);
  private readonly cvMap = inject(CvMapService);
  private readonly store = inject(Store);
  private readonly componentDestroyed = new Subject<void>();

  protected readonly markers = this.signalStore.markers;
  protected readonly markers$ = toObservable(this.markers);
  protected readonly markerWrappers$ = toObservable(this.clusterableCvMarkers);
  protected readonly customMarker = this.signalStore.customMarker;
  protected readonly autocompleteMarker = this.signalStore.autocompleteMarker;
  protected readonly mapsFullyRendered = this.signalStore.mapsFullyRendered;
  protected readonly streetViewActive = this.store.selectSignal(selectIsStreetViewActive);
  protected readonly streetViewLoading = this.signalStore.streetViewLoading;
  protected readonly mapLoading = this.signalStore.mapLoading;
  protected readonly streetViewClosedByUser = this.store.selectSignal(selectIsStreetViewClosedByUser);
  protected readonly showValidationHistory = this.store.selectSignal(selectShowValidationHistory);
  protected readonly focusedLocation = this.store.selectSignal(selectFocusedLocation);
  protected readonly infoWindowMarker = this.signalStore.infoWindowMarker;
  protected readonly continueAfterValidation = this.signalStore.continueAfterValidation;
  protected readonly canContinueAfterValidation = this.store.selectSignal(selectCanContinueAfterValidation);
  protected readonly cvButtonActive = this.store.selectSignal(selectIsCvButtonActive);
  protected readonly markersMinimized = this.store.selectSignal(selectAreMarkersMinimized);
  protected readonly rightPanelWidth = this.store.selectSignal(selectRightPanelRelativeWidth);
  protected readonly hoveredMarkerId = this.signalStore.hoveredMarkerId;
  protected readonly validationHistory = this.store.selectSignal(selectValidationHistory);
  protected readonly validationHistoryLoading = this.store.selectSignal(selectValidationHistoryLoading);
  protected readonly lastValidation = this.signalStore.lastValidation;

  protected readonly MAP_OPTIONS = MAP_OPTIONS(true);
  constructor() {
    // Handle map bounds (map centering)
    // Here coz moving to signal store creates circular dependency
    effect(() => {
      const markers = this.markers();
      const mapsFullyRendered = this.mapsFullyRendered();

      if (mapsFullyRendered) {
        // untracked as otherwise, this effect would be triggered by all signals
        // handleMapBoundsAndStreetViewPosition touches
        untracked(() => {
          this.cvMap.handleMapBoundsAndStreetViewPosition(
              markers,
              this.streetViewClosedByUser(),
          ).then();
        });
      }
    });

    // Close info window when there's no infoWindowMarkerId in the store
    effect(() => {
      const closeInfoWindow = !this.signalStore.infoWindowMarkerId();
      const infoWindow = this.infoWindow();

      if (closeInfoWindow && infoWindow?.infoWindow) {
        infoWindow.close();
      }
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.cvMap.initStreetView(this.streetViewContainer().nativeElement,
          this.googleMapDirective().googleMap);
    }, 100);

    // Handle Clustering on marker change
    this.clustererDirective().markerClustererInitialized
        .pipe(
            combineLatestWith(
                // wrappers don't emit a change unless they were re-rendered,
                this.markerWrappers$,
                // watch the underlying markers array in case their position changes, like
                // accepting a diff can change the position of the validated marker
                this.markers$),
            delay(0),
            takeUntil(this.componentDestroyed))
        .subscribe(([, wrappers]) => {
          const clustererInstance = this.clustererDirective()?.markerClusterer;
          // Extract the actual Google AdvancedMarkerElements
          const nativeMarkers = wrappers
              .map(w => w.mapAdvancedMarker()?.advancedMarker)
              .filter(m => !!m);
          // Push them into the clusterer instance
          // Content projection in the clusterer component does not work
          // correctly since we're using wrappers around <advanced-map-marker>
          clustererInstance.clearMarkers();
          clustererInstance.addMarkers(nativeMarkers);
        });
  }

  ngOnDestroy() {
    const clusterer = this.clustererDirective()?.markerClusterer;

    if (clusterer) {
      clusterer.clearMarkers();
      clusterer.setMap(null);
    }

    this.componentDestroyed.next();
  }

  // ************************************* EVENT HANDLERS *************************************** //

  /**
   * Set data src for info window and open it
   * @param markerElement
   * @param markerId
   * @protected
   */
  protected openCvInfoWindow(markerElement: MapAdvancedMarker, markerId: string) {
    if (markerId === AUTOCOMPLETE_MARKER_ID) return;

    this.signalStore.updateInfoWindowMarkerId(markerId);
    this.infoWindow().open(markerElement, true);
  }

  protected closeInfoWindow() {
    this.infoWindow().close();
  }

  /**
   * Validate / Invalidate / Revalidate coordinates
   * @param marker
   * @protected
   */
  protected processMarkerAction(marker: MapMarkerData): void {
    this.signalStore.processMarkerAction(marker);
  }

  /**
   * Update Custom marker position
   * @param marker
   * @param e
   */
  protected onMapDrag(marker: MapMarkerData, e: google.maps.MapMouseEvent) {
    if (marker.id !== CvMarkerType.CUSTOM) {
      return;
    }

    const markerCopy = cloneDeep(marker) as MapMarkerData;
    markerCopy.position = e.latLng.toJSON();
    this.signalStore.updateCustomMarker(markerCopy);
  }

  /**
   * Close street view - will stretch the default map and initiate street view map teardown logic
   */
  protected closeStreetView() {
    this.googleMapDirective().getStreetView().setVisible(false);
    this.store.dispatch(MapActions.updateStreetViewClosedByUser({ streetViewClosedByUser: true }));
  }

  /**
   * Set hovered marker ID
   * @param markerId
   */
  protected onCvMarkerMouseover(markerId: string) {
    this.signalStore.updateHoveredMarkerId(markerId);
  }

  /**
   * Unset hovered marker ID
   */
  protected onCvMarkerMouseout() {
    this.signalStore.updateHoveredMarkerId(null);
  }

  /**
   * BoundsChanged should set this to true, idle to false
   * Avoid doing heavy operations like rendering markers or clustering while map is loading
   * https://mapsplatform.google.com/resources/blog/google-maps-platform-best-practices-optimization-and-performance-tips/
   * @param loading
   */
  protected updateMapLoading(loading: boolean) {
    this.signalStore.updateMapLoading(loading);
  }

  /**
   * Toggle CV mode
   */
  protected onToggleCvMode() {
    this.store.dispatch(MapActions.toggleCvButton());
  }

  /**
   * Update showValidationHistory
   */
  protected onShowHistory(showValidationHistory: boolean) {
    this.store.dispatch(MapActions.updateShowValidationHistory({ showValidationHistory }));
  }

  /**
   * Toggle minimized / full-sized marker icons
   */
  protected onToggleMinimizeMarkers() {
    this.store.dispatch(MapActions.toggleMarkersMinimizedButton());
  }

  /**
   * Place a marker on the map with the position from the places autocomplete search box
   */
  protected onPlaceAutocompleteMarker(e: PlaceAutocompleteMarkerEvent) {
    if (e) {
      this.signalStore.updateAutocompleteMarker({
        id: AUTOCOMPLETE_MARKER_ID,
        type: CvMarkerType.AUTOCOMPLETE,
        position: e.position,
      });
    } else this.signalStore.updateAutocompleteMarker(null);
  }

  /**
   * Place a custom coordinates marker on the map
   * @protected
   */
  protected onPlaceCustomMarker(customMarker: MapMarkerData) {
    this.signalStore.updateCustomMarker(customMarker);
  }

  /**
   * Map UI event handler
   * @param continueAfterValidation
   */
  protected onContinueAfterValidation(continueAfterValidation: boolean) {
    this.signalStore.updateContinueAfterValidation(continueAfterValidation);
  }

}
